import { useEffect, useReducer, useRef, useState } from 'react';
import { fetchManagerCallStream } from '@/store/api/call';
import {
  managerTranscriptMonitorInitialState,
  parseSseEventBlock,
  reduceManagerTranscriptState,
  type ManagerTranscriptMonitorState,
} from './managerTranscriptMonitorState';

const RECONNECT_BACKOFF_MS = [1000, 2000, 5000, 10000];

type UseManagerTranscriptMonitorResult = {
  state: ManagerTranscriptMonitorState;
  restart: () => void;
};

export function useManagerTranscriptMonitor(
  contactId: string | undefined,
  enabled: boolean,
  isOutbound = false
): UseManagerTranscriptMonitorResult {
  const [state, dispatch] = useReducer(
    reduceManagerTranscriptState,
    managerTranscriptMonitorInitialState
  );
  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const endedRef = useRef(false);
  const [restartCounter, setRestartCounter] = useState(0);

  const clearResources = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const restart = () => {
    endedRef.current = false;
    setRestartCounter(prev => prev + 1);
    dispatch({ type: 'reset' });
  };

  useEffect(() => {
    if (!enabled || !contactId) {
      clearResources();
      dispatch({ type: 'reset' });
      return;
    }

    let cancelled = false;
    let attempt = 0;

    const openStream = async () => {
      if (cancelled || endedRef.current) return;
      clearResources();
      dispatch(
        attempt > 0 ? { type: 'reconnecting', attempt } : { type: 'connecting' }
      );

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetchManagerCallStream(
          contactId,
          controller.signal,
          isOutbound
        );
        if (!response.ok || !response.body) {
          if (
            response.status >= 400 &&
            response.status < 500 &&
            response.status !== 429
          ) {
            dispatch({ type: 'error' });
            clearResources();
            return;
          }
          throw new Error(`Manager stream failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled && !endedRef.current) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';

          for (const block of blocks) {
            const evt = parseSseEventBlock(block);
            if (!evt) continue;
            if (evt.type === 'ping') continue;
            dispatch({ type: 'event', event: evt });
            if (evt.type === 'stream_end') {
              endedRef.current = true;
              dispatch({ type: 'stream_end' });
              clearResources();
              return;
            }
          }
        }

        if (!endedRef.current && !cancelled) {
          throw new Error('Manager stream ended unexpectedly');
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        if (endedRef.current) return;

        attempt += 1;
        const delay =
          RECONNECT_BACKOFF_MS[
            Math.min(attempt - 1, RECONNECT_BACKOFF_MS.length - 1)
          ];
        dispatch({ type: 'reconnecting', attempt });
        reconnectTimerRef.current = window.setTimeout(() => {
          void openStream();
        }, delay);
      }
    };

    void openStream();

    return () => {
      cancelled = true;
      clearResources();
    };
  }, [contactId, enabled, restartCounter, isOutbound]);

  return { state, restart };
}
